import React from 'react';
import { Box, Typography, Iconography } from '@lucid/lego';
import type { Theme } from '../utils';
import styles from './EngagementHubTab.module.scss';
import {
  ProspectActivity,
  ProspectActivityType,
} from '../../../../../api/services/lupp/prospect/dto.prospect';
import { formatDateTime, formatString } from '../../../../../utils/tools';
import { getActivityTypeLabel } from '../../../constants';

const COMMENT_PREVIEW_LENGTH = 100;
const COPIED_FEEDBACK_DURATION_MS = 1500;

const CONTACT_US_ACTIVITY_TYPES = new Set<string>([
  ProspectActivityType.AGENCY_CAPTURE_FORM,
  ProspectActivityType.CONTACT_US,
  ProspectActivityType.GENERAL_CONTACT_FORM,
]);

/** Activity types whose description is shown below the timestamp. */
const DESCRIPTION_ACTIVITY_TYPES = new Set<string>([
  ...CONTACT_US_ACTIVITY_TYPES,
  ProspectActivityType.LOCATION_CHANGED,
]);

type EngagementHubTabProps = {
  theme: Theme;
  labels?: Record<string, string>;
  prospectActivities?: ProspectActivity[];
  locale?: string;
  timezone?: string;
};

/** Fills the {{from}} placeholder of a "changed from" label, falling back to English text. */
const fillChangedFrom = (label: string | undefined, fallbackPrefix: string, from: string): string =>
  label?.replace('{{from}}', from) ?? `${fallbackPrefix} ${from}`;

/** "Rescheduled from …" note for a test drive history entry, or null if not rescheduled. */
const getRescheduledFromText = (
  previousDateTime: string | null | undefined,
  labels: Record<string, string> | undefined,
  locale: string,
  timezone?: string
): string | null => {
  if (!previousDateTime) return null;
  const from = formatDateTime(previousDateTime, locale, undefined, timezone);
  return fillChangedFrom(labels?.['engagementHub.rescheduledFrom'], 'Rescheduled from', from);
};

/** "Location changed from …" note for a test drive history entry, or null if unchanged. */
const getLocationChangedFromText = (
  previousLocation: string | null | undefined,
  labels: Record<string, string> | undefined
): string | null => {
  const from = previousLocation?.trim();
  if (!from) return null;
  return fillChangedFrom(
    labels?.['engagementHub.locationChangedFrom'],
    'Location changed from',
    from
  );
};

/**
 * Engagement Hub tab content component
 * Displays engagement timeline and communication context information
 * @component
 * @param props - Component props
 * @param props.theme - Theme for the component
 * @param props.labels - i18n labels
 * @param props.prospectActivities - List of prospect activities to display
 * @param props.locale - Locale used for date formatting
 * @param props.timezone - Prospect's timezone
 * @returns JSX.Element
 */
const EngagementHubTab: React.FC<EngagementHubTabProps> = ({
  theme,
  labels,
  prospectActivities = [],
  locale = 'en-US',
  timezone,
}) => {
  const [expandedComments, setExpandedComments] = React.useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const copiedTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleComment = React.useCallback((itemId: string) => {
    setExpandedComments((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  }, []);

  React.useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    };
  }, []);

  const getDescriptionText = React.useCallback(
    (id: string, description: string) => {
      const isExpanded = expandedComments[id] ?? false;
      if (description.length <= COMMENT_PREVIEW_LENGTH || isExpanded) {
        return description;
      }

      return `${description.slice(0, COMMENT_PREVIEW_LENGTH)}...`;
    },
    [expandedComments]
  );

  const copyDesignShareCode = React.useCallback(
    async (itemId: string, shortCode?: string | null) => {
      if (!shortCode) return;
      try {
        await navigator.clipboard.writeText(shortCode);
        setCopiedId(itemId);
        if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
        copiedTimeoutRef.current = setTimeout(() => setCopiedId(null), COPIED_FEEDBACK_DURATION_MS);
      } catch (error) {
        console.error('Failed to copy design share code', error);
      }
    },
    []
  );

  return (
    <Box className={styles.engagementHubTab} data-testid="engagement-hub-tab" data-theme={theme}>
      <Box mb={8}>
        <Typography variant="headline" size="small" color="white" theme={theme}>
          {labels?.['details.engagementHub'] ?? 'Engagement Hub'}
        </Typography>
      </Box>

      <Box className={styles.description} mb={16}>
        <Typography variant="body" size="medium" color="white" theme={theme}>
          {labels?.['engagementHub.description'] ??
            'Vertical timeline of past and upcoming engagements, including automated web and system events.'}
        </Typography>
      </Box>

      <Box className={styles.sectionDivider} aria-hidden="true" />

      <Box mt={16}>
        <Typography
          variant="body"
          size="small"
          color="gray-5"
          theme={theme}
          className={styles.sectionTitle}
        >
          {labels?.['engagementHub.pastActivities'] ?? 'PAST ACTIVITIES'}
        </Typography>
      </Box>

      <Box className={styles.timelineList} mt={12}>
        {prospectActivities.length === 0 ? (
          <Typography variant="body" size="medium" color="gray-5" theme={theme}>
            {labels?.['engagementHub.noActivities'] ?? 'No activities yet'}
          </Typography>
        ) : (
          [...prospectActivities]
            .sort((a, b) => {
              const dateA = new Date(a.activityTimestamp || '').getTime();
              const dateB = new Date(b.activityTimestamp || '').getTime();
              return dateB - dateA;
            })
            .map((activity, index) => {
              const id = activity.prospectActivityIdentifier ?? `activity-${index}`;
              const isTestDriveActivity =
                activity.activityType === ProspectActivityType.TEST_DRIVE_FORM;
              const isDesignShareActivity =
                activity.activityType === ProspectActivityType.SHARE_DESIGN_FORM;
              const activityType = activity.activityType ?? '';
              const isContactUsActivity = CONTACT_US_ACTIVITY_TYPES.has(activityType);
              const showsDescription = DESCRIPTION_ACTIVITY_TYPES.has(activityType);

              const activityTypeTitle = getActivityTypeLabel(
                activity.activityType,
                labels,
                activity.activityTypeName?.trim() ||
                  formatString(activity.activityType as string) ||
                  (labels?.['engagementHub.activity'] ?? 'Activity')
              );

              const activitySourceName = activity.activitySource?.trim();
              const shouldShowRecordedVia =
                !isTestDriveActivity && !isDesignShareActivity && !isContactUsActivity;
              const title =
                activitySourceName && shouldShowRecordedVia
                  ? `${activityTypeTitle}`
                  : activityTypeTitle;

              const timestamp = activity.activityTimestamp
                ? formatDateTime(activity.activityTimestamp, locale, undefined, timezone)
                : '-';
              const description =
                activity.activityDescription?.trim() || activity.activityDetail?.comments?.trim();
              const isLast = index === prospectActivities.length - 1;

              const activityDetails =
                isTestDriveActivity || isDesignShareActivity || isContactUsActivity
                  ? activity.activityDetail
                  : null;

              const testDriveDateTime =
                activityDetails?.scheduledStartDateTime ?? activityDetails?.appointmentDateTime;
              const testDriveTimestamp = testDriveDateTime
                ? formatDateTime(testDriveDateTime, locale, undefined, timezone)
                : '-';

              // History entries for a changed booking carry the values it changed from.
              const rescheduledFromText = getRescheduledFromText(
                activityDetails?.previousScheduledStartDateTime,
                labels,
                locale,
                timezone
              );
              const locationChangedFromText = getLocationChangedFromText(
                activityDetails?.previousLocation,
                labels
              );

              const vehicleInfo =
                activityDetails?.vehicle?.trim() || activityDetails?.interestedTrim?.trim();
              const designShareShortCode = activityDetails?.shortCode?.trim();
              const showDesignShareShortCode = Boolean(designShareShortCode);
              const shortCodeSuffix = showDesignShareShortCode ? `(${designShareShortCode})` : '';
              const designShareDisplayText = showDesignShareShortCode
                ? [vehicleInfo, shortCodeSuffix].filter(Boolean).join(' ')
                : vehicleInfo;

              return (
                <Box
                  key={id}
                  className={styles.activityItem}
                  data-testid="engagement-activity-item"
                >
                  <Box className={styles.markerColumn} aria-hidden="true">
                    <span className={styles.markerDot} />
                    {!isLast && <span className={styles.markerLine} />}
                  </Box>

                  <Box className={styles.activityCard}>
                    <Box className={styles.activityHeaderRow}>
                      <Typography
                        variant="body"
                        size="large"
                        color="white"
                        theme={theme}
                        className={styles.activityTitle}
                      >
                        {title}
                      </Typography>
                      {isDesignShareActivity && activityDetails ? (
                        <Box className={styles.designShareRow}>
                          <Typography
                            variant="body"
                            size="small"
                            color="gray-4"
                            theme={theme}
                            className={styles.designShareText}
                          >
                            {designShareDisplayText}
                          </Typography>
                          {showDesignShareShortCode && (
                            <Box className={styles.designShareCopyWrapper}>
                              {copiedId === id && (
                                <output className={styles.copiedTooltip}>
                                  {labels?.['engagementHub.copiedDesignShareCode'] ?? 'Copied!'}
                                </output>
                              )}
                              <button
                                type="button"
                                className={styles.designShareCopyButton}
                                onClick={() => copyDesignShareCode(id, designShareShortCode)}
                                data-testid={`copy-design-share-${id}`}
                                aria-label={
                                  labels?.['engagementHub.copyDesignShareCode'] ??
                                  'Copy design share code'
                                }
                              >
                                <Iconography
                                  category="global"
                                  name="copy"
                                  color="gray-5"
                                  theme={theme}
                                  dataTestId={`design-share-copy-icon-${id}`}
                                />
                              </button>
                            </Box>
                          )}
                        </Box>
                      ) : isContactUsActivity && activityDetails && vehicleInfo ? (
                        <Typography
                          variant="body"
                          size="small"
                          color="gray-4"
                          theme={theme}
                          className={styles.designShareText}
                        >
                          {vehicleInfo}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography variant="body" size="small" color="gray-4" theme={theme}>
                      {timestamp}
                    </Typography>
                    {isTestDriveActivity && activityDetails && (
                      <Box className={styles.activityTestDriveCard}>
                        <Typography
                          variant="body"
                          size="small"
                          color="white"
                          theme={theme}
                          className={styles.activityTestDriveStatusTitle}
                        >
                          {activityDetails.status?.trim() ?? '-'}
                        </Typography>
                        <Typography
                          variant="body"
                          size="small"
                          color="white"
                          theme={theme}
                          className={styles.activityTestDriveModel}
                        >
                          {vehicleInfo}
                        </Typography>
                        <Typography variant="body" size="small" color="white" theme={theme}>
                          {testDriveTimestamp}
                        </Typography>
                        <Typography
                          variant="body"
                          size="small"
                          color="white"
                          theme={theme}
                          className={styles.activityTestDriveLocation}
                        >
                          {activityDetails.location?.trim() ?? '-'}
                        </Typography>
                        {rescheduledFromText && (
                          <Typography
                            variant="body"
                            size="small"
                            color="gray-4"
                            theme={theme}
                            data-testid={`test-drive-rescheduled-from-${id}`}
                          >
                            {rescheduledFromText}
                          </Typography>
                        )}
                        {locationChangedFromText && (
                          <Typography
                            variant="body"
                            size="small"
                            color="gray-4"
                            theme={theme}
                            data-testid={`test-drive-location-changed-from-${id}`}
                          >
                            {locationChangedFromText}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {description && showsDescription && (
                      <Box className={styles.descriptionBlock}>
                        <Typography variant="body" size="medium" color="white" theme={theme}>
                          {getDescriptionText(id, description)}
                        </Typography>
                        {description.length > COMMENT_PREVIEW_LENGTH && (
                          <button
                            type="button"
                            className={styles.toggleCommentButton}
                            onClick={() => toggleComment(id)}
                            data-testid={`toggle-comment-${id}`}
                          >
                            {expandedComments[id]
                              ? (labels?.['engagementHub.showLess'] ?? 'Show less')
                              : (labels?.['engagementHub.showMore'] ?? 'Show more')}
                          </button>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })
        )}
      </Box>
    </Box>
  );
};

export default EngagementHubTab;
